import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Input, Textarea, Button, Spinner } from '@cloudscape-design/components';
import { PuzzleInfoWithIdAndDetails, PuzzleUpdate } from '../../../common/types';
import { updatePuzzle } from '../../../common/api/puzzles';

export type PuzzleViewProps = {
  puzzle: PuzzleInfoWithIdAndDetails;
};

export function PuzzleView(props: PuzzleViewProps) {
  // Initialize local state from the puzzle prop.
  const [puzzle, setPuzzle] = useState<PuzzleInfoWithIdAndDetails>(props.puzzle);
  const [editedName, setEditedName] = useState(puzzle.name);
  const [editedDescription, setEditedDescription] = useState(puzzle.description);
  const [editedSolution, setEditedSolution] = useState(puzzle.solution);

  // Create a mutation for updating the puzzle details.
  const updateMutation = useMutation({
    mutationFn: async ({ puzzleUuid, update }: { puzzleUuid: string; update: PuzzleUpdate }) =>
      updatePuzzle(puzzleUuid, update),
    onSuccess: () => {
      // Update local state after a successful mutation.
      setPuzzle((prev) => ({
        ...prev,
        name: editedName,
        description: editedDescription,
        solution: editedSolution,
      }));
    },
  });

  const handleSave = useCallback(() => {
    updateMutation.mutate({
      puzzleUuid: puzzle.puzzleUuid,
      update: {
        name: editedName.trim(),
        description: editedDescription.trim(),
        solution: editedSolution.trim(),
      },
    });
  }, [editedName, editedDescription, editedSolution, puzzle.puzzleUuid, updateMutation]);

  const loading = updateMutation.isPending;
  const changed =
    editedName !== puzzle.name ||
    editedDescription !== puzzle.description ||
    editedSolution !== puzzle.solution;

  return (
    <div className="bg-gray-700 p-4 rounded-lg shadow-md">
      {/* Puzzle Header with Number and Editable Name */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4 sm:w-1/2">
          <div className="rounded-full bg-gray-900 flex items-center justify-center text-white font-bold w-10 h-10">
            <span>{puzzle.number}</span>
          </div>
          <div className="flex-1">
            <label className="block text-lg font-medium text-gray-200">Puzzle Name</label>
            <Input
              className="w-full"
              value={editedName}
              disabled={loading}
              onChange={(e) => setEditedName(e.detail.value)}
              placeholder="Puzzle name..."
            />
          </div>
        </div>
      </div>

      {/* Editable Description */}
      <div className="mb-4">
        <label className="block text-lg font-medium text-gray-200">Description</label>
        <Textarea
          value={editedDescription}
          disabled={loading}
          onChange={(e) => setEditedDescription(e.detail.value)}
          placeholder="Puzzle description..."
          rows={4}
        />
      </div>

      {/* Editable Solution */}
      <div className="mb-4">
        <label className="block text-lg font-medium text-gray-200">Solution</label>
        <Textarea
          value={editedSolution}
          disabled={loading}
          onChange={(e) => setEditedSolution(e.detail.value)}
          placeholder="Puzzle solution..."
          rows={4}
        />
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          className={`text-black font-semibold text-sm py-2 px-4 rounded ${
            loading || !changed ? 'bg-gray-400' : 'bg-white hover:bg-gray-200'
          }`}
          disabled={loading || !changed}
          onClick={handleSave}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <Spinner />
              <span className="ml-2">Saving...</span>
            </div>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </div>
  );
}
